import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useCatalog } from '@/hooks/useCatalog';
import { seoForPath } from '@/seo/seo';

function setMeta(selector: string, attrs: Record<string, string>, content: string | null): void {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (content === null) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/** Keeps the head right as the visitor moves around without a page load, from
 *  the same words the server sent on the first response (seo/seo.ts). */
export function useDocumentHead(): void {
  const { pathname } = useLocation();
  const needsCatalog = pathname.startsWith('/product/');
  const { data: catalog } = useCatalog({ enabled: needsCatalog });

  useEffect(() => {
    if (needsCatalog && !catalog) return;
    const seo = seoForPath(pathname, catalog ?? []);
    document.title = seo.title;
    setMeta('meta[name="description"]', { name: 'description' }, seo.description);
    setMeta('meta[property="og:title"]', { property: 'og:title' }, seo.title);
    setMeta('meta[property="og:description"]', { property: 'og:description' }, seo.description);
    setMeta('meta[property="og:url"]', { property: 'og:url' }, seo.canonical);
    setMeta('meta[property="og:image"]', { property: 'og:image' }, seo.image);
    setMeta('meta[name="robots"]', { name: 'robots' }, seo.indexable ? null : 'noindex, nofollow');

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (seo.canonical) {
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = seo.canonical;
    } else {
      canonical?.remove();
    }

    document.head.querySelectorAll('script[type="application/ld+json"]').forEach((s) => s.remove());
    for (const block of seo.jsonLd) {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.textContent = JSON.stringify(block);
      document.head.appendChild(s);
    }
  }, [pathname, needsCatalog, catalog]);
}
